import React, { useRef, useEffect } from "react";
import { useState } from "react";

interface WaveformCanvasProps {
    analyser: AnalyserNode | null;
    width?: number;
    height?: number;
}

const WaveformCanvas: React.FC<WaveformCanvasProps> = ({ analyser }) => {
    const divRef = useRef<HTMLDivElement>(null);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [width, setWidth] = useState(0);
    const [height, setHeight] = useState(0);

    useEffect(() => {
        if (!analyser) return;
        if (!width || !height) {
            setWidth(divRef.current?.clientWidth || 240);
            setHeight(divRef.current?.clientHeight || 48);
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        let animationId: number;
        const bufferLength = analyser.fftSize;
        const dataArray = new Uint8Array(bufferLength);

        function draw() {
            if (!analyser || !canvas || !ctx) return;
            analyser.getByteTimeDomainData(dataArray);
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.lineWidth = 2;
            ctx.strokeStyle = "#222";
            ctx.beginPath();

            const midY = canvas.height / 2;
            const sliceWidth = canvas.width / bufferLength;
            let x = 0;
            for (let i = 0; i < bufferLength; i++) {
                const v = dataArray[i] / 128.0;
                const y = midY - (v - 1) * (midY - 2);
                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
                x += sliceWidth;
            }
            x = 0;
            ctx.lineTo(canvas.width, midY);
            ctx.moveTo(0, midY);
            for (let i = 0; i < bufferLength; i++) {
                const v = dataArray[i] / 128.0;
                const y = midY + (v - 1) * (midY - 2);
                if (i === 0) {
                    ctx.moveTo(x, y);
                } else {
                    ctx.lineTo(x, y);
                }
                x += sliceWidth;
            }
            ctx.stroke();
            animationId = requestAnimationFrame(draw);
        }
        draw();
        return () => {
            if (animationId) cancelAnimationFrame(animationId);
            if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
        };
    }, [analyser, width, height]);

    return (
        <div ref={divRef} style={{ width: '100%', height: '100%' }}>
            <canvas
                ref={canvasRef}
                width={width}
                height={height}
                style={{
                    display: "block",
                    margin: "0 auto",
                    background: "transparent",
                    borderRadius: 16,
                }}
            />
        </div>
    );
};

export default WaveformCanvas;
